/**
 * Single source of truth for the colored "assignee chip" rendered across
 * the WBS table, the migration-task list, the DAG canvas, and the
 * settings page. Centralized so that:
 *
 *   1. Adding a new view that surfaces an assignee is a one-import job.
 *   2. The same person renders with the same color everywhere, anchored
 *      on `AssigneeDoc.colorIndex` (or, for legacy free-text rows, the
 *      deterministic `assigneeColorIndex(name)` hash from shared).
 *   3. Visual tweaks (size, border, hover) ship to all surfaces at once.
 *
 * Two render paths:
 *
 *   <AssigneeChip assignee={doc} />     // preferred — frozen color
 *   <AssigneeChip name="鈴木" />        // legacy — name-derived color
 *
 * Pass `null`/`undefined` to render the muted "未割当" fallback.
 */
import type { AssigneeDoc } from '@mct/shared';
import { resolveAssigneeColorClasses, UNASSIGNED_CLASSES } from '../../lib/assigneeColor';
import { cn } from '../../lib/utils';

export interface AssigneeChipProps {
  /** Preferred input — when present, color comes from the doc's frozen `colorIndex`. */
  assignee?: Pick<AssigneeDoc, 'name' | 'colorIndex'> | null;
  /**
   * Fallback display name. Used both as the rendered label when no
   * `assignee` doc was passed, and as the input to the deterministic
   * hash when a legacy WBS row only has a name.
   */
  name?: string | null;
  /** Optional placeholder when the assignee is unset. Defaults to "未割当". */
  placeholder?: string;
  /** Size variant. `sm` (default) matches inline cells; `md` is for headings. */
  size?: 'sm' | 'md';
  /** Optional click handler — turns the chip into a button (no styling change). */
  onClick?: () => void;
  /** Additional Tailwind classes to merge on top of the chip styles. */
  className?: string;
  /** Native `title` attribute; defaults to the resolved name. */
  title?: string;
}

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'px-1.5 py-0 text-[11px]',
  md: 'px-2 py-0.5 text-xs',
};

export function AssigneeChip({
  assignee,
  name,
  placeholder = '未割当',
  size = 'sm',
  onClick,
  className,
  title,
}: AssigneeChipProps): JSX.Element {
  // Resolve the label: prefer the doc's name (canonical) over the prop,
  // because the doc is the source of truth post-backfill.
  const resolvedName = assignee?.name ?? name ?? null;
  const isAssigned = Boolean(resolvedName);

  // Resolve the color classes via the centralized helper so the doc's
  // frozen `colorIndex` wins over any name-hash drift.
  const colorClasses = isAssigned
    ? resolveAssigneeColorClasses(assignee ?? null, name ?? null)
    : UNASSIGNED_CLASSES;

  const baseClasses = cn(
    'inline-flex max-w-full items-center truncate rounded-full font-medium leading-5',
    SIZE_CLASSES[size],
    colorClasses,
    !isAssigned && 'italic',
    onClick && 'cursor-pointer hover:brightness-95',
    className,
  );

  const label = resolvedName ?? placeholder;
  const resolvedTitle = title ?? label;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={baseClasses}
        title={resolvedTitle}
        data-testid="assignee-chip"
      >
        {label}
      </button>
    );
  }

  return (
    <span
      className={baseClasses}
      title={resolvedTitle}
      data-testid="assignee-chip"
    >
      {label}
    </span>
  );
}
